import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiBriefcase, FiEye } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { companiesAPI, usersAPI } from '../../services/api';
import Modal from '../../components/Common/Modal';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { UnifiedFilterHeaderCell, UnifiedTableToolbar } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';

const COMPANY_LIST_PAGE_SIZE = 50;

const PROSPECT_TEMPERATURE_OPTIONS = ['Cold', 'Warm', 'Hot'];
const PARTNER_STATUS_OPTIONS = ['Active', 'Former'];
const RELATIONSHIP_STAGE_OPTIONS = ['job_dating', 'ongoing_placement', 'active_search', 'priority_placement'];

const CompanyList = () => {
  const { t } = useTranslation();
  const { canPerformAction } = useAuth();
  const navigate = useNavigate();
  const canCreateCompanies = canPerformAction('companies', 'create');
  const canUpdateCompanies = canPerformAction('companies', 'update');
  const canDeleteCompanies = canPerformAction('companies', 'delete');
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const [filterProspectTemperature, setFilterProspectTemperature] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);

  const emptyForm = {
    company_name: '', siret_number: '', company_address: '', company_phone: '', company_email: '',
    contact_first_name: '', contact_last_name: '', contact_position: '', contact_phone: '', contact_email: '',
    relationship_manager_id: '', prospect_temperature: 'Cold', partner_status: 'Active',
    has_open_position: false, relationship_stage: ''
  };
  const [form, setForm] = useState(emptyForm);

  const companyColumns = useMemo(() => ([
    { key: 'company', label: t('companies.tableCompany') },
    { key: 'country_code', label: t('common.countryCode', 'Country Code') },
    { key: 'company_phone', label: t('companies.companyPhone') },
    { key: 'company_email', label: t('companies.companyEmail') },
    { key: 'phone', label: t('common.phone', 'Phone'), defaultVisible: false },
    { key: 'email', label: t('common.email', 'Email'), defaultVisible: false },
    { key: 'main_contact', label: t('companies.tableMainContact') },
    { key: 'contact_phone', label: t('companies.contactPhone'), defaultVisible: false },
    { key: 'contact_email', label: t('companies.contactEmail'), defaultVisible: false },
    { key: 'relationship_manager', label: t('companies.tableRelationshipManager') },
    { key: 'assigned_employee', label: t('common.assignedEmployee', 'Assigned Employee'), defaultVisible: false },
    { key: 'lead_status', label: t('common.leadStatus', 'Lead Status'), defaultVisible: false },
    { key: 'status', label: t('companies.tableStatus') },
    { key: 'notes', label: t('common.notes', 'Notes'), defaultVisible: false },
    { key: 'created_at', label: t('common.createdAt', 'Created At'), defaultVisible: false },
    { key: 'updated_at', label: t('common.updatedAt', 'Updated At'), defaultVisible: false },
    { key: 'actions', label: t('common.actions', 'Actions'), locked: true }
  ]), [t]);
  const { isVisible, toggleColumn, resetToDefault, columns: colDefs } = useColumnPreferences('companies', companyColumns);

  useEffect(() => {
    usersAPI.getSalesTeam().then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!form.company_name) { toast.error(t('companies.nameRequired')); return; }
    try {
      const payload = {
        ...form,
        relationship_manager_id: form.relationship_manager_id || null,
        has_open_position: !!form.has_open_position
      };
      if (editing) {
        await companiesAPI.update(editing.company_id, payload);
        toast.success(t('companies.updated'));
      } else {
        await companiesAPI.create(payload);
        toast.success(t('companies.created'));
      }
      setShowModal(false);
      setEditing(null);
      setForm(emptyForm);
      loadCompanies();
    } catch (err) { toast.error(err.response?.data?.message || t('common.failed')); }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      company_name: c.company_name || '',
      siret_number: c.siret_number || '',
      company_address: c.company_address || '',
      company_phone: c.company_phone || '',
      company_email: c.company_email || '',
      contact_first_name: c.contact_first_name || '',
      contact_last_name: c.contact_last_name || '',
      contact_position: c.contact_position || '',
      contact_phone: c.contact_phone || '',
      contact_email: c.contact_email || '',
      relationship_manager_id: c.relationship_manager_id || '',
      prospect_temperature: c.prospect_temperature || 'Cold',
      partner_status: c.partner_status || 'Active',
      has_open_position: Number(c.has_open_position) === 1,
      relationship_stage: c.relationship_stage || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };

  const handleDelete = async (id) => {
    if (!window.confirm(t('companies.deleteConfirm'))) return;
    try { await companiesAPI.delete(id); toast.success(t('companies.deleted')); loadCompanies(); }
    catch { toast.error(t('common.failed')); }
  };

  const getStatusColor = (s) => {
    const map = { Cold: 'var(--info)', Warm: 'var(--warning)', Hot: 'var(--danger)' };
    return map[s] || 'var(--text-muted)';
  };

  const prospectTemperatureLabel = (s) => {
    const key = { Cold: 'companies.statuses.cold', Warm: 'companies.statuses.warm', Hot: 'companies.statuses.hot' }[s];
    return key ? t(key) : (s || '-');
  };
  const companyFilterColumns = useMemo(() => {
    const ownerOptions = Array.from(new Set(companies.map((c) => String(c.relationship_manager_name || '')).filter(Boolean)));
    return [
      { key: 'company_name', label: t('companies.tableCompany'), field: 'company_name', type: 'text' },
      { key: 'main_contact', label: t('companies.tableMainContact'), field: 'contact_first_name', type: 'text' },
      { key: 'relationship_manager_name', label: t('companies.tableRelationshipManager'), field: 'relationship_manager_name', type: 'multi', options: ownerOptions.map((o) => ({ value: o, label: o })) },
      { key: 'prospect_temperature', label: t('companies.tableStatus'), field: 'prospect_temperature', type: 'multi', options: PROSPECT_TEMPERATURE_OPTIONS.map((s) => ({ value: s, label: prospectTemperatureLabel(s) })) },
      { key: 'notes', label: t('common.notes', 'Notes'), field: 'notes', type: 'text' },
      { key: 'created_at', label: t('common.createdAt', 'Created At'), field: 'created_at', type: 'date' },
      { key: 'updated_at', label: t('common.updatedAt', 'Updated At'), field: 'updated_at', type: 'date' }
    ];
  }, [companies, t]);
  const companyFilterColumnsRef = useLatest(companyFilterColumns);
  const filteredCompanies = useMemo(
    () =>
      runTableFilters(
        companies,
        tableFilters,
        tableFilters,
        ['company_name', 'contact_first_name', 'contact_last_name', 'company_email', 'relationship_manager_name', 'notes'],
        companyFilterColumns
      ),
    [companies, tableFilters, companyFilterColumns]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, companyFilterColumns),
    [tableFilters, companyFilterColumns]
  );
  const pagedFilteredCompanies = useMemo(() => {
    if (!clientTableFilterMode) return filteredCompanies;
    const start = (page - 1) * COMPANY_LIST_PAGE_SIZE;
    return filteredCompanies.slice(start, start + COMPANY_LIST_PAGE_SIZE);
  }, [clientTableFilterMode, filteredCompanies, page]);
  const listTotalForPaging = clientTableFilterMode ? filteredCompanies.length : total;
  const totalPages = Math.max(1, Math.ceil(listTotalForPaging / COMPANY_LIST_PAGE_SIZE));

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(listTotalForPaging / COMPANY_LIST_PAGE_SIZE));
    if (page > tp) setPage(tp);
  }, [clientTableFilterMode, filteredCompanies.length, total, page]);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const params = {
        limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : COMPANY_LIST_PAGE_SIZE,
        page: active ? 1 : page,
        prospect_temperature: filterProspectTemperature || undefined
      };
      const res = await companiesAPI.getAll(params);
      setCompanies(res.data.companies || []);
      setTotal(res.data.total || 0);
    } catch { toast.error(t('companies.failedLoad')); }
    setLoading(false);
  }, [filterProspectTemperature, page, t, clientTableFilterMode]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const partnerStatusLabel = (s) => {
    const key = { Active: 'companies.partnerStatuses.active', Former: 'companies.partnerStatuses.former' }[s];
    return key ? t(key) : (s || '-');
  };
  const relationshipStageLabel = (s) => {
    const key = {
      job_dating: 'companies.relationshipStages.jobDating',
      ongoing_placement: 'companies.relationshipStages.ongoingPlacement',
      active_search: 'companies.relationshipStages.activeSearch',
      priority_placement: 'companies.relationshipStages.priorityPlacement'
    }[s];
    return key ? t(key) : '-';
  };
  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiBriefcase style={{ marginRight: 8 }} />{t('companies.title')}</h2>
          <p className="page-subtitle">{t('companies.companiesCount', { count: clientTableFilterMode ? listTotalForPaging : total })}</p>
        </div>
        {canCreateCompanies && <button className="btn btn-primary" onClick={handleAdd}><FiPlus /> {t('companies.addBtn')}</button>}
      </div>

      <div className="filter-bar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <UnifiedTableToolbar
          draft={tableFilters}
          onDraftChange={(updater) => {
            setTableFilters(updater);
            setPage(1);
          }}
          onReset={() => setTableFilters({ globalSearch: '', columns: {} })}
          hasActiveFilters={Boolean(tableFilters.globalSearch || Object.keys(tableFilters.columns || {}).length)}
          searchPlaceholder={t('companies.searchPlaceholder')}
        />
        <select value={filterProspectTemperature} onChange={(e) => { setFilterProspectTemperature(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{t('companies.allStatuses')}</option>
          {PROSPECT_TEMPERATURE_OPTIONS.map(s => <option key={s} value={s}>{prospectTemperatureLabel(s)}</option>)}
        </select>
        <ColumnCustomizer
          columns={colDefs}
          isVisible={isVisible}
          toggleColumn={toggleColumn}
          resetToDefault={resetToDefault}
        />
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="table-container">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  {isVisible('company') && <UnifiedFilterHeaderCell column={companyFilterColumns[0]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('country_code') && <th>{t('common.countryCode', 'Country Code')}</th>}
                  {isVisible('company_phone') && <th>{t('companies.companyPhone')}</th>}
                  {isVisible('company_email') && <th>{t('companies.companyEmail')}</th>}
                  {isVisible('phone') && <th>{t('common.phone', 'Phone')}</th>}
                  {isVisible('email') && <th>{t('common.email', 'Email')}</th>}
                  {isVisible('main_contact') && <UnifiedFilterHeaderCell column={companyFilterColumns[1]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('contact_phone') && <th>{t('companies.contactPhone')}</th>}
                  {isVisible('contact_email') && <th>{t('companies.contactEmail')}</th>}
                  {isVisible('relationship_manager') && <UnifiedFilterHeaderCell column={companyFilterColumns[2]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('assigned_employee') && <th>{t('common.assignedEmployee', 'Assigned Employee')}</th>}
                  {isVisible('lead_status') && <th>{t('common.leadStatus', 'Lead Status')}</th>}
                  {isVisible('status') && <UnifiedFilterHeaderCell column={companyFilterColumns[3]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('notes') && <UnifiedFilterHeaderCell column={companyFilterColumns[4]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('created_at') && <UnifiedFilterHeaderCell column={companyFilterColumns[5]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('updated_at') && <UnifiedFilterHeaderCell column={companyFilterColumns[6]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  <th>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedFilteredCompanies.map(c => (
                  <tr key={c.company_id}>
                    {isVisible('company') && (
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 700 }}
                          onClick={() => navigate(`/companies/${c.company_id}`)}
                          title={t('common.view', 'View')}
                        >
                          {c.company_name}
                        </button>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.siret_number || '-'}</div>
                      </td>
                    )}
                    {isVisible('country_code') && <td>{c.country_code || '-'}</td>}
                    {isVisible('company_phone') && <td>{c.company_phone || '-'}</td>}
                    {isVisible('company_email') && <td>{c.company_email || '-'}</td>}
                    {isVisible('phone') && <td>{c.phone || '-'}</td>}
                    {isVisible('email') && <td>{c.email || '-'}</td>}
                    {isVisible('main_contact') && (
                      <td>
                        <div>{[c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || '-'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.contact_position || '-'}</div>
                      </td>
                    )}
                    {isVisible('contact_phone') && <td>{c.contact_phone || '-'}</td>}
                    {isVisible('contact_email') && <td>{c.contact_email || '-'}</td>}
                    {isVisible('relationship_manager') && <td>{c.relationship_manager_name || '-'}</td>}
                    {isVisible('assigned_employee') && <td>{c.assigned_employee_name || c.assigned_employee_id || '-'}</td>}
                    {isVisible('lead_status') && <td>{c.lead_status || '-'}</td>}
                    {isVisible('status') && (
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            color: 'var(--white)', background: getStatusColor(c.prospect_temperature), width: 'fit-content'
                          }}>{prospectTemperatureLabel(c.prospect_temperature)}</span>
                          <span style={{ fontSize: 12 }}>{partnerStatusLabel(c.partner_status)}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {Number(c.has_open_position) === 1 ? t('common.yes') : t('common.no')} · {relationshipStageLabel(c.relationship_stage)}
                          </span>
                        </div>
                      </td>
                    )}
                    {isVisible('notes') && (
                      <td style={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.notes || ''}>
                        {c.notes || '-'}
                      </td>
                    )}
                    {isVisible('created_at') && <td>{formatDateTime(c.created_at)}</td>}
                    {isVisible('updated_at') && <td>{formatDateTime(c.updated_at)}</td>}
                    <td>
                      <div className="table-actions">
                        <button className="view" title={t('common.view', 'View')} onClick={() => navigate(`/companies/${c.company_id}`)}><FiEye /></button>
                        {canUpdateCompanies && <button className="edit" title={t('common.edit')} onClick={() => handleEdit(c)}><FiEdit /></button>}
                        {canDeleteCompanies && <button className="delete" title={t('common.delete')} onClick={() => handleDelete(c.company_id)}><FiTrash2 /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCompanies.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>{t('companies.noCompanies')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t('common.prev')}</button>
              <span style={{ padding: '6px 12px', fontSize: 13 }}>{t('common.page', { current: page, total: totalPages })}</span>
              <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('common.next')}</button>
            </div>
          )}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? t('companies.editTitle') : t('companies.addTitle')}
        footer={<>
          <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={handleSubmit}>{editing ? t('common.update') : t('common.create')}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.generalInformation')}</h4>
          </div>
          <div className="form-group full-width">
            <label>{t('companies.companyName')} <span className="required">*</span></label>
            <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.siret')}</label>
            <input value={form.siret_number} onChange={e => setForm(f => ({ ...f, siret_number: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('companies.companyAddress')}</label>
            <textarea value={form.company_address} onChange={e => setForm(f => ({ ...f, company_address: e.target.value }))} rows={3} />
          </div>
          <div className="form-group">
            <label>{t('companies.companyPhone')}</label>
            <input value={form.company_phone} onChange={e => setForm(f => ({ ...f, company_phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.companyEmail')}</label>
            <input type="email" value={form.company_email} onChange={e => setForm(f => ({ ...f, company_email: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.mainContact')}</h4>
          </div>
          <div className="form-group">
            <label>{t('companies.contactFirstName')}</label>
            <input value={form.contact_first_name} onChange={e => setForm(f => ({ ...f, contact_first_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactLastName')}</label>
            <input value={form.contact_last_name} onChange={e => setForm(f => ({ ...f, contact_last_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactPosition')}</label>
            <input value={form.contact_position} onChange={e => setForm(f => ({ ...f, contact_position: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactPhone')}</label>
            <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactEmail')}</label>
            <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.relationshipManager')}</h4>
          </div>
          <div className="form-group full-width">
            <label>{t('companies.relationshipManagerName')}</label>
            <select value={form.relationship_manager_id} onChange={e => setForm(f => ({ ...f, relationship_manager_id: e.target.value }))}>
              <option value="">{t('companies.noneAssigned')}</option>
              {employees.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.companyStatus')}</h4>
          </div>
          <div className="form-group">
            <label>{t('companies.prospectTemperature')}</label>
            <select value={form.prospect_temperature} onChange={e => setForm(f => ({ ...f, prospect_temperature: e.target.value }))}>
              {PROSPECT_TEMPERATURE_OPTIONS.map(s => <option key={s} value={s}>{prospectTemperatureLabel(s)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.partnerStatus')}</label>
            <select value={form.partner_status} onChange={e => setForm(f => ({ ...f, partner_status: e.target.value }))}>
              {PARTNER_STATUS_OPTIONS.map(s => <option key={s} value={s}>{partnerStatusLabel(s)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.hasOpenPosition')}</label>
            <select value={form.has_open_position ? '1' : '0'} onChange={e => setForm(f => ({ ...f, has_open_position: e.target.value === '1' }))}>
              <option value="0">{t('common.no')}</option>
              <option value="1">{t('common.yes')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.relationshipStage')}</label>
            <select value={form.relationship_stage} onChange={e => setForm(f => ({ ...f, relationship_stage: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {RELATIONSHIP_STAGE_OPTIONS.map(s => <option key={s} value={s}>{relationshipStageLabel(s)}</option>)}
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CompanyList;
